'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from './atoms/button';
import { PhoneNumberInput } from './PhoneNumberInput';

const phoneSchema = z.object({
  phoneNumber: z.string().min(1, 'Phone number is required'),
});

type PhoneFormData = z.infer<typeof phoneSchema>;

interface PhoneNumberFormProps {
  onSubmit: (phoneNumber: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  language: 'en' | 'de';
}

export const PhoneNumberForm: React.FC<PhoneNumberFormProps> = (props) => {
  const { onSubmit, loading, error, language } = props;
  const form = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
  });

  const handleSubmit = async (data: PhoneFormData) => {
    await onSubmit(data.phoneNumber);
  };

  // Translations
  const translations = {
    en: {
      phoneTitle: 'Sign In with Phone',
      phoneDescription:
        'Enter your phone number to receive a verification code',
      phoneLabel: 'Phone Number',
      phonePlaceholder: '+1 (555) 123-4567',
      sendButton: 'Send Verification Code',
      sendingButton: 'Sending...',
    },
    de: {
      phoneTitle: 'Mit Telefon anmelden',
      phoneDescription:
        'Geben Sie Ihre Telefonnummer ein, um einen Bestätigungscode zu erhalten',
      phoneLabel: 'Telefonnummer',
      phonePlaceholder: '+49 176 1234 1234',
      sendButton: 'Bestätigungscode senden',
      sendingButton: 'Wird gesendet...',
    },
  };

  const t = translations[language];

  return (
    <>
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight">{t.phoneTitle}</h1>
        <p className="text-white/70">{t.phoneDescription}</p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Controller
          name="phoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneNumberInput
              placeholder={t.phonePlaceholder}
              label={t.phoneLabel}
              error={form.formState.errors.phoneNumber?.message}
              value={field.value || ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              countryCode={language === 'de' ? 'DE' : 'US'}
            />
          )}
        />

        {error && (
          <div className="text-center text-sm text-red-500">{error}</div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-white text-black hover:bg-white/90 disabled:opacity-50"
        >
          <span>{loading ? t.sendingButton : t.sendButton}</span>
        </Button>
      </form>
    </>
  );
};
